import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Star, MapPin } from "lucide-react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Eyebrow from "@/components/common/Eyebrow";
import { GOLD } from "@/theme";

const goldIcon = new L.DivIcon({
  className: "",
  html: `<div style="width:34px;height:34px;border-radius:50%;background:${GOLD};color:#fff;display:flex;align-items:center;justify-content:center;font-family:Outfit,sans-serif;font-size:11px;font-weight:600;box-shadow:0 6px 20px -4px rgba(0,0,0,0.4);border:2px solid #fff;">◆</div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});
const activeIcon = new L.DivIcon({
  className: "",
  html: `<div style="width:46px;height:46px;border-radius:50%;background:#0A0A0A;color:${GOLD};display:flex;align-items:center;justify-content:center;font-family:Playfair Display,serif;font-size:16px;font-weight:600;box-shadow:0 12px 30px -4px rgba(0,0,0,0.5);border:2px solid ${GOLD};">◆</div>`,
  iconSize: [46, 46],
  iconAnchor: [23, 23],
});

function FitBounds({ villas }) {
  const map = useMap();
  useEffect(() => {
    if (!villas.length) return;
    const bounds = L.latLngBounds(villas.map((v) => [v.coordinates.lat, v.coordinates.lng]));
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [villas, map]);
  return null;
}

export default function MapPage() {
  const [villas, setVillas] = useState([]);
  const [hoverId, setHoverId] = useState(null);

  useEffect(() => {
    api.get("/villas?limit=100").then((r) => setVillas(r.data));
  }, []);

  const center = useMemo(() => (villas.length ? [villas[0].coordinates.lat, villas[0].coordinates.lng] : [15.3, 74.1]), [villas]);

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { lg: "420px 1fr" }, height: "calc(100vh - 80px)" }} data-testid="map-page">
      <Box component="aside" sx={{ overflowY: "auto", borderRight: 1, borderColor: "divider" }}>
        <Box sx={{ px: 3, py: 4, borderBottom: 1, borderColor: "divider", position: "sticky", top: 0, bgcolor: "background.default", zIndex: 10 }}>
          <Eyebrow sx={{ mb: 1 }}>Explore</Eyebrow>
          <Typography className="font-serif-display" sx={{ fontSize: 30 }}>Map view</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>{villas.length} villas across India</Typography>
        </Box>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {villas.map((v) => {
            const price = v.discount_percent ? Math.round(v.price_per_night * (1 - v.discount_percent / 100)) : v.price_per_night;
            const active = hoverId === v.id;
            return (
              <Box
                key={v.id}
                component={Link}
                to={`/villas/${v.id}`}
                data-testid={`map-card-${v.id}`}
                onMouseEnter={() => setHoverId(v.id)}
                onMouseLeave={() => setHoverId(null)}
                sx={{
                  display: "flex", gap: 2, p: 1.5, borderRadius: 4, textDecoration: "none", color: "inherit",
                  border: 1, borderColor: active ? "primary.main" : "transparent",
                  bgcolor: active ? (t) => (t.palette.mode === "dark" ? "rgba(212,175,55,0.08)" : "rgba(197,160,89,0.05)") : "transparent",
                  "&:hover": { borderColor: active ? "primary.main" : "divider" },
                }}
              >
                <Box component="img" src={v.images[0]} alt="" sx={{ width: 96, height: 96, borderRadius: 3, objectFit: "cover", flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography className="font-serif-display" sx={{ fontSize: 18, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.name}</Typography>
                  <Box sx={{ fontSize: 12, color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                    <MapPin size={10} />{v.location}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, fontSize: 12 }}>
                    <Star size={11} className="text-gold" fill="currentColor" />{v.rating} <Box component="span" sx={{ color: "text.secondary" }}>({v.reviews_count})</Box>
                  </Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                    ₹{price.toLocaleString()}<Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}> /night</Box>
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box sx={{ position: "relative", minHeight: 400 }}>
        <MapContainer center={center} zoom={5} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          <FitBounds villas={villas} />
          {villas.map((v) => (
            <Marker
              key={v.id}
              position={[v.coordinates.lat, v.coordinates.lng]}
              icon={hoverId === v.id ? activeIcon : goldIcon}
              eventHandlers={{
                mouseover: () => setHoverId(v.id),
                mouseout: () => setHoverId(null),
              }}
            >
              <Popup>
                <div style={{ fontFamily: "Outfit,sans-serif" }}>
                  <div style={{ fontFamily: "Playfair Display,serif", fontSize: 16, marginBottom: 4 }}>{v.name}</div>
                  <div style={{ fontSize: 11, opacity: 0.7 }}>{v.location}</div>
                  <Link to={`/villas/${v.id}`} style={{ color: GOLD, fontSize: 12, marginTop: 6, display: "inline-block" }}>View villa →</Link>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </Box>
    </Box>
  );
}
